import Model, { attr, hasMany, type HasMany } from '@ember-data/model';
import type { Type } from '@warp-drive/core-types/symbols';
import type RoadmapModel from './roadmap';

export const TOTAL_WORKING_DAYS = 260;
export default class InvestmentScenarioModel extends Model {
  declare [Type]: 'investment-scenario';
  @attr('string') declare title: string;
  @attr declare start_date: string;
  @attr declare end_date: string;
  @attr('number') declare fte: number;
  @attr('number', {
    defaultValue: 50,
  })
  declare initiative_capacity: number; //percentage

  @attr('number') declare ops_flex: number;

  @attr('number') declare distribution_vision: number;
  @attr('number') declare distribution_customer: number;
  @attr('number') declare distribution_operations: number;

  @attr('number') declare limit_lower_vision: number;
  @attr('number') declare limit_upper_vision: number;

  @attr('number') declare limit_lower_customer: number;
  @attr('number') declare limit_upper_customer: number;

  @attr('number') declare limit_lower_operations: number;
  @attr('number') declare limit_upper_operations: number;

  @attr('number') declare price_person_week: number;

  @attr declare allocation: unknown;

  @attr declare unavailable_time: { total: number | string } | null;

  @hasMany('roadmap', {
    async: false,
    inverse: 'investment-scenario',
  })
  declare roadmaps: HasMany<RoadmapModel>;

  get total_distribution() {
    return (
      this.distribution_vision +
      this.distribution_customer +
      this.distribution_operations
    );
  }

  get percentage_flow() {
    if (!this.unavailable_portion) return 100 - +this.initiative_capacity;
    return Math.round(
      ((100 - +this.unavailable_portion) * (100 - +this.initiative_capacity)) /
        100,
    );
  }

  get percentage_unavailable() {
    return +this.unavailable_portion;
  }

  get percentage_initiatives() {
    if (!this.unavailable_portion) return +this.initiative_capacity;
    return Math.round(
      ((100 - +this.unavailable_portion) * +this.initiative_capacity) / 100,
    );
  }

  get weeks() {
    return 13;
  }

  get pw_flow() {
    return this.fte * (this.percentage_flow / 100) * this.weeks;
  }

  get pw_unavailable() {
    return Math.round(
      this.fte * (this.percentage_unavailable / 100) * this.weeks,
    );
  }

  get pw_initiatives() {
    return this.pw_total - (this.pw_flow + this.pw_unavailable);
  }

  get pw_vision() {
    return this.pw_number(this.distribution_vision);
  }

  get pw_customer() {
    return this.pw_number(this.distribution_customer);
  }

  get pw_operations() {
    return this.pw_number(this.distribution_operations);
  }

  get pw_total() {
    return this.pw_number();
  }

  pw_number(source?: number) {
    if (source == undefined) {
      return this.fte * this.weeks;
    } else {
      return (
        (source / this.total_distribution) *
        this.fte *
        (this.percentage_initiatives / 100) *
        this.weeks
      );
    }
  }

  get unavailable_portion() {
    let percent_unavailable = 0;
    if (this.unavailable_time) {
      const total_unavailable = +this.unavailable_time.total;
      percent_unavailable = Math.floor(
        (total_unavailable / TOTAL_WORKING_DAYS) * 100,
      );
    }
    return percent_unavailable;
  }

  copy_to_object(new_scenario: InvestmentScenarioModel) {
    new_scenario.title = this.title + ' (copy)';
    new_scenario.start_date = this.start_date;
    new_scenario.end_date = this.end_date;
    new_scenario.fte = this.fte;
    new_scenario.initiative_capacity = this.initiative_capacity;
    new_scenario.ops_flex = this.ops_flex;
    new_scenario.distribution_vision = this.distribution_vision;
    new_scenario.distribution_customer = this.distribution_customer;
    new_scenario.distribution_operations = this.distribution_operations;

    new_scenario.limit_lower_vision = this.limit_lower_vision;
    new_scenario.limit_upper_vision = this.limit_upper_vision;

    new_scenario.limit_lower_customer = this.limit_lower_customer;
    new_scenario.limit_upper_customer = this.limit_upper_customer;

    new_scenario.limit_lower_operations = this.limit_lower_operations;
    new_scenario.limit_upper_operations = this.limit_upper_operations;
    return new_scenario;
  }
}
